import { googleMapsReady } from 'etc/googleMapsLoader'

export interface AddressResult {
  addressLine: string
  adminDistrict: string
  countryRegion: string
  countryRegionIso2: string
  formattedAddress: string
  locality: string
  postalCode: string
}
export interface GeoCodeResult {
  authenticationResultCode: string
  brandLogoUri: string
  copyright: string
  resourceSets: {
    estimatedTotal: number
    resources: {
      address: AddressResult | undefined
      bbox: number[]
      confidence: string
      entityType: string
      matchCodes: string[]
      name: string
      geocodePoints: {
        calculationMethod: string
        coordinates: number[]
        type: string
        usageTypes: string[]
      }[]
      point: {
        coordinates: number[]
        type: string
      }
    }[]
  }[]
  statusCode: number
  statusDescription: string
  traceId: string
}
export interface MapCoordinates {
  id: string
  Comments?: string | undefined
  Latitude: number
  Longitude: number
  RelatedFormId?: number | undefined
}
export interface MapPoint {
  altitude?: number
  altitudeReference?: number
  latitude?: number
  longitude?: number
}

export interface AddressSuggestion {
  label: string;
  latitude: number;
  longitude: number;
  address?: any;
}

/**
 * Separates "the request ran and there genuinely are no matches" from "the
 * request never ran at all" -- billing not enabled on the Cloud project, the
 * Places/Geocoding API not enabled for the key, an HTTP-referrer restriction
 * that doesn't cover this origin, or exhausted quota.
 *
 * Both cases used to collapse into an empty array / undefined, which made a hard
 * configuration failure indistinguishable from a typo: the address field showed
 * "Invalid Input", and a caller that fell back to "keep whatever coordinates are
 * already set" silently kept a point the user never picked.
 */
export interface AddressLookupResult {
  suggestions: AddressSuggestion[]
  /** '' on success (a legitimate zero-result search included); otherwise the
   *  google.maps.places.PlacesServiceStatus string, e.g. 'REQUEST_DENIED'. */
  failureStatus: string
}

export interface ReverseGeocodeResult {
  address: any | undefined
  /** '' on success (a legitimate zero-result lookup included); otherwise the
   *  google.maps.GeocoderStatus string, e.g. 'REQUEST_DENIED'. */
  failureStatus: string
}

/** True for statuses that mean "the service itself is unavailable", as opposed
 *  to "your query matched nothing". Callers use this to show a service-level
 *  message instead of blaming the user's input. */
export const isMapServiceUnavailable = (failureStatus: string): boolean =>
  !!failureStatus && failureStatus !== 'ZERO_RESULTS'

// Reshapes Google's address_components into the field names the old Azure Maps
// responses used (freeformAddress, municipality, countrySubdivision, etc) so
// every downstream consumer (AddressField.tsx, SitesLocations.tsx) keeps working
// against an Azure-shaped object without any changes on their end.
const getComponent = (
  components: google.maps.GeocoderAddressComponent[] | undefined,
  type: string,
  useShortName = false
): string => {
  const match = components?.find((c) => c.types.includes(type))
  if (!match) return ''
  return useShortName ? match.short_name : match.long_name
}

const mapAddressComponents = (components: google.maps.GeocoderAddressComponent[] | undefined) => ({
  streetNumber: getComponent(components, 'street_number'),
  streetName: getComponent(components, 'route'),
  municipality: getComponent(components, 'locality') || getComponent(components, 'postal_town'),
  municipalitySubdivision: getComponent(components, 'sublocality') || getComponent(components, 'neighborhood'),
  countrySubdivision: getComponent(components, 'administrative_area_level_1', true),
  countrySubdivisionName: getComponent(components, 'administrative_area_level_1'),
  country: getComponent(components, 'country'),
  countryRegion: getComponent(components, 'country', true),
  postalCode: getComponent(components, 'postal_code'),
})

/**
 * Builds the label a picked suggestion is stored under. A place's own name is
 * what the user searched for and recognises ("Emporium Mall"), but
 * formatted_address alone drops it entirely and leaves a bare street address --
 * so the name is kept in front of it, exactly like the map's POI-click label
 * (see onPlaceClick in GoogleMap.tsx). Skipped when formatted_address already
 * begins with the name, the normal case for plain street addresses.
 */
const buildPlaceLabel = (
  name: string | undefined,
  formattedAddress: string | undefined,
  fallback: string
): string => {
  const address = (formattedAddress || '').trim()
  const placeName = (name || '').trim()
  if (!placeName) return address || fallback
  if (!address) return placeName
  if (address.toLowerCase().startsWith(placeName.toLowerCase())) return address
  return `${placeName}, ${address}`
}

let placesServiceDiv: HTMLDivElement | undefined

// Cached once per page load (not per keystroke) -- biases address-search results
// toward the user's actual current position so typing a local area/street (e.g.
// "Johar Town" while physically in Lahore) ranks it above same-named places
// elsewhere in the world, without excluding other countries outright the way a
// hard componentRestrictions filter would.
let currentLocationBias: Promise<google.maps.CircleLiteral | undefined> | undefined
const getCurrentLocationBias = (): Promise<google.maps.CircleLiteral | undefined> => {
  if (!currentLocationBias) {
    currentLocationBias = new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve(undefined)
        return
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          center: { lat: position.coords.latitude, lng: position.coords.longitude },
          radius: 50000,
        }),
        () => resolve(undefined),
        { enableHighAccuracy: false, maximumAge: 300000, timeout: 5000 }
      )
    })
  }
  return currentLocationBias
}

export class mappingService {

  reverseGeocodeWithStatus = async (lat: number, lon: number): Promise<ReverseGeocodeResult> => {
    const google = await googleMapsReady
    const geocoder = new google.maps.Geocoder()

    return new Promise<ReverseGeocodeResult>((resolve) => {
      geocoder.geocode({ location: { lat, lng: lon } }, (results, status) => {
        if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) {
          resolve({ address: undefined, failureStatus: String(status) })
          return
        }

        const result = results[0]
        resolve({
          address: {
            ...mapAddressComponents(result.address_components),
            freeformAddress: result.formatted_address || '',
          },
          failureStatus: '',
        })
      })
    })
  };

  reverseGeocode = async (lat: number, lon: number) =>
    (await this.reverseGeocodeWithStatus(lat, lon)).address;

  addressLookupWithStatus = async (query: string): Promise<AddressLookupResult> => {
    if (!query || query.trim().length < 2) return { suggestions: [], failureStatus: '' };

    const google = await googleMapsReady
    const autocompleteService = new google.maps.places.AutocompleteService()
    const locationBias = await getCurrentLocationBias()

    const { predictions, status } = await new Promise<{
      predictions: google.maps.places.AutocompletePrediction[]
      status: string
    }>((resolve) => {
      autocompleteService.getPlacePredictions(
        { input: query, ...(locationBias ? { locationBias } : {}) },
        (results, requestStatus) => {
          if (requestStatus === google.maps.places.PlacesServiceStatus.OK && results) {
            resolve({ predictions: results, status: '' })
            return
          }
          resolve({ predictions: [], status: String(requestStatus) })
        }
      )
    })

    if (status) return { suggestions: [], failureStatus: status };
    if (predictions.length === 0) return { suggestions: [], failureStatus: '' };

    // PlacesService needs a map or a div to attach to -- a detached div works fine
    // since we never render it.
    if (!placesServiceDiv) {
      placesServiceDiv = document.createElement('div')
    }
    const placesService = new google.maps.places.PlacesService(placesServiceDiv)

    const detailFailures: string[] = []
    const suggestions = await Promise.all(
      predictions.slice(0, 10).map(
        (prediction) =>
          new Promise<AddressSuggestion | undefined>((resolve) => {
            placesService.getDetails(
              { placeId: prediction.place_id, fields: ['name', 'geometry', 'formatted_address', 'address_components'] },
              (place, detailStatus) => {
                if (detailStatus !== google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) {
                  detailFailures.push(String(detailStatus))
                  resolve(undefined)
                  return
                }

                resolve({
                  label: buildPlaceLabel(place.name, place.formatted_address, prediction.description),
                  latitude: place.geometry.location.lat(),
                  longitude: place.geometry.location.lng(),
                  address: {
                    ...mapAddressComponents(place.address_components),
                    freeformAddress: place.formatted_address || '',
                  },
                })
              }
            )
          })
      )
    )

    const resolved = suggestions.filter((s): s is AddressSuggestion => s != null)

    // Predictions came back but not one of them could be resolved to a point --
    // that's a Place Details failure, not an empty search, and the caller must
    // not report it as "no such place".
    return {
      suggestions: resolved,
      failureStatus: resolved.length === 0 && detailFailures.length > 0 ? detailFailures[0] : '',
    }
  };

  addressLookupDS = async (query: string): Promise<AddressSuggestion[]> =>
    (await this.addressLookupWithStatus(query)).suggestions;
}
